import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ClaimStatus, ClaimType, EhrClaim } from '../../interfaces/billing';
import { SelectOption } from '../../interfaces/common';
import { AuthService, BillingService } from '../../services';
import { Roles } from '../../util/constants';
import { toSelectOptions } from '../../util/eligibility';
import { BulkRunAbortError, bulkRun } from '../../utils/bulkRun';
import { PaginateAbortError, paginateAll } from '../../utils/paginateAll';
import { CustomTable } from '../common/CustomTable/CustomTable';
import { Column, RowId } from '../common/CustomTable/types';
import { BulkActionsToolbar } from './BulkActionsToolbar';
import { ClaimStatusIcon } from './ClaimStatusIcon';

interface ClaimsListProps {
  clinicOptions: SelectOption[];
  onClaimClick: (claim: EhrClaim) => void;
  onMessage: (message: string) => void;
}

const getClaimClinicId = (claim: EhrClaim): number | undefined => {
  return claim.clinicId ?? (claim as unknown as { clinic_id?: number }).clinic_id;
};

const getPatientLabel = (claim: EhrClaim): string => {
  if (!claim.corePatient?.firstName) return 'Unknown';
  return `${claim.corePatient.firstName} ${claim.corePatient.lastName ?? ''}`.trim();
};

const COLUMNS: Column[] = [
  { key: 'serviceDate', label: 'Service date', minWidth: 110 },
  { key: 'patient', label: 'Patient', minWidth: 160 },
  { key: 'status', label: 'Status', minWidth: 130 },
  { key: 'source', label: 'Type', minWidth: 110 },
  { key: 'clinic', label: 'Clinic', minWidth: 140 },
];

const PAGE_SIZE = 50;
const DEFAULT_FROM_DATE = moment().subtract(30, 'days').format('YYYY-MM-DD');
const DEFAULT_UNTIL_DATE = moment().format('YYYY-MM-DD');

export const ClaimsList: React.FC<ClaimsListProps> = ({
  clinicOptions,
  onClaimClick,
  onMessage,
}) => {
  const history = useHistory();
  const location = useLocation();

  const [clinicIds, setClinicIds] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([
    ClaimStatus.Pending,
    ClaimStatus.Uploaded,
    ClaimStatus.UploadedWithErrors,
    ClaimStatus.Processed,
  ]);
  const [types, setTypes] = useState<string[]>([
    ClaimType.Appointment,
    ClaimType.RPM,
    ClaimType.Unknown,
  ]);
  const [fromDate, setFromDate] = useState<string>(DEFAULT_FROM_DATE);
  const [untilDate, setUntilDate] = useState<string>(DEFAULT_UNTIL_DATE);

  const [claims, setClaims] = useState<EhrClaim[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageProgress, setPageProgress] = useState<{ page: number; totalPages: number } | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const [selectedRowIds, setSelectedRowIds] = useState<RowId[]>([]);
  const [busy, setBusy] = useState(false);
  const fetchAbortRef = useRef<AbortController | null>(null);

  const canDelete = AuthService.checkRoleAccess(Roles.Operations);

  // Patient filter is URL-driven so it survives tab switches and navigation
  const patientIds = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('patientIds');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [location.search]);

  const setPatientIds = (next: string[]) => {
    const search = new URLSearchParams(location.search);
    if (next.length === 0) {
      search.delete('patientIds');
    } else {
      search.set('patientIds', next.join(','));
    }
    const qs = search.toString();
    history.replace({ pathname: location.pathname, search: qs ? `?${qs}` : '' });
  };

  // Auto-pick first clinic if none selected (mirrors calendar behavior)
  useEffect(() => {
    if (clinicIds.length === 0 && clinicOptions.length > 0) {
      setClinicIds([clinicOptions[0].value]);
    }
  }, [clinicOptions, clinicIds.length]);

  const clinicLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    clinicOptions.forEach((c) => {
      map[c.value] = c.label;
    });
    return map;
  }, [clinicOptions]);

  // Patient options are derived from the loaded claims so the filter always
  // reflects what's actually shown.
  const patientOptions = useMemo<SelectOption[]>(() => {
    const seen = new Map<string, string>();
    for (const claim of claims) {
      const id = claim.corePatient?.id;
      if (id == null) continue;
      const key = `${id}`;
      if (!seen.has(key)) seen.set(key, getPatientLabel(claim));
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [claims]);

  const handleApply = async () => {
    if (clinicIds.length === 0) {
      onMessage('Select at least one clinic');
      return;
    }
    if (!fromDate || !untilDate) {
      onMessage('Set both From and Until dates');
      return;
    }

    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setLoading(true);
    setPageProgress(null);
    setSelectedRowIds([]);
    setPage(1);

    try {
      const service = new BillingService();
      const baseParams = {
        fromDate,
        untilDate,
        clinicIds: clinicIds.join(','),
        ...(statuses.length && { statuses: statuses.join(',') }),
        ...(types.length && { sources: types.join(',') }),
      };

      const all = await paginateAll<EhrClaim>(
        async (p) => {
          const response = await service.getClaims({ ...baseParams, page: p, perPage: 100 });
          if (!response) return null;
          if (!response.success) {
            throw new Error(response.message || 'Error fetching claims');
          }
          return { data: response.claims, pagination: response.pagination };
        },
        {
          signal: controller.signal,
          onProgress: (p, totalPages) => setPageProgress({ page: p, totalPages }),
        },
      );

      if (controller.signal.aborted) return;
      setClaims(all);
    } catch (error) {
      if (error instanceof PaginateAbortError) return;
      onMessage(`Error fetching claims: ${(error as Error).message}`);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setPageProgress(null);
      }
    }
  };

  const selectedIdsAsNumbers = useMemo(
    () => selectedRowIds.map((id) => Number(id)),
    [selectedRowIds],
  );

  // Resync is only valid for non-Processed claims, matching the single-claim
  // rule in ClaimDetails. Processed claims in the selection are skipped.
  const statusBySelectedId = useMemo(() => {
    const selectedSet = new Set(selectedIdsAsNumbers);
    const map = new Map<number, ClaimStatus>();
    for (const c of claims) {
      if (selectedSet.has(c.id)) map.set(c.id, c.status);
    }
    return map;
  }, [claims, selectedIdsAsNumbers]);

  const resyncableSelectedIds = useMemo(
    () =>
      selectedIdsAsNumbers.filter(
        (id) => statusBySelectedId.get(id) !== ClaimStatus.Processed,
      ),
    [selectedIdsAsNumbers, statusBySelectedId],
  );

  const handleBulkResync = async () => {
    if (resyncableSelectedIds.length === 0) return;
    setBusy(true);
    try {
      const service = new BillingService();
      const result = await bulkRun(
        resyncableSelectedIds,
        async (claimId) => {
          const response = await service.reSyncEhrClaim(claimId);
          if (!response || !response.success) {
            throw new Error(response?.message || `Resync failed for ${claimId}`);
          }
        },
        { concurrency: 3 },
      );
      const skipped = selectedIdsAsNumbers.length - resyncableSelectedIds.length;
      const skippedSuffix = skipped > 0 ? ` (${skipped} processed skipped)` : '';
      const summary =
        result.failed.length === 0
          ? `Resynced ${result.succeeded.length} claim${
              result.succeeded.length === 1 ? '' : 's'
            }${skippedSuffix}.`
          : `Resynced ${result.succeeded.length} of ${resyncableSelectedIds.length}; ${result.failed.length} failed${skippedSuffix}.`;
      onMessage(summary);
      setSelectedRowIds([]);
      // Refresh list to surface updated statuses
      await handleApply();
    } catch (err) {
      if (err instanceof BulkRunAbortError) return;
      onMessage(`Bulk resync error: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIdsAsNumbers.length === 0) return;
    setBusy(true);
    try {
      const service = new BillingService();
      const result = await bulkRun(
        selectedIdsAsNumbers,
        async (claimId) => {
          const response = await service.deleteEhrClaim(claimId);
          if (!response || !response.success) {
            throw new Error(response?.message || `Delete failed for ${claimId}`);
          }
        },
        { concurrency: 3 },
      );
      const summary =
        result.failed.length === 0
          ? `Deleted ${result.succeeded.length} claim${result.succeeded.length === 1 ? '' : 's'}.`
          : `Deleted ${result.succeeded.length} of ${selectedIdsAsNumbers.length}; ${result.failed.length} failed.`;
      onMessage(summary);
      const succeededIds = new Set(result.succeeded.map((id) => id));
      setClaims((prev) => prev.filter((c) => !succeededIds.has(c.id)));
      setSelectedRowIds([]);
    } catch (err) {
      if (err instanceof BulkRunAbortError) return;
      onMessage(`Bulk delete error: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  // Client-side patient filter so options derived from loaded data and the
  // applied filter stay in sync, and the filter survives navigation even when
  // the underlying claims are replaced.
  const filteredClaims = useMemo(() => {
    if (patientIds.length === 0) return claims;
    const allowed = new Set(patientIds);
    return claims.filter((c) => c.corePatient?.id != null && allowed.has(`${c.corePatient.id}`));
  }, [claims, patientIds]);

  const visibleClaims = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredClaims.slice(start, start + PAGE_SIZE);
  }, [filteredClaims, page]);

  // Reset paging when the filter narrows the result set below the current page
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(filteredClaims.length / PAGE_SIZE));
    if (page > lastPage) setPage(1);
  }, [filteredClaims.length, page]);

  const rows = useMemo(
    () =>
      visibleClaims.map((claim) => {
        const cid = getClaimClinicId(claim);
        const clinicLabel =
          cid != null ? clinicLabelById[`${cid}`] ?? `#${cid}` : '—';
        return {
          id: claim.id,
          serviceDate: moment.utc(claim.serviceDate).format('YYYY-MM-DD'),
          patient: getPatientLabel(claim),
          status: (
            <Box display='flex' alignItems='center' gap={0.5}>
              <ClaimStatusIcon status={claim.status} source={claim.source} fontSize='small' />
              <Typography variant='body2' component='span'>
                {claim.status}
              </Typography>
            </Box>
          ),
          source: claim.source,
          clinic: clinicLabel,
          onClick: () => onClaimClick(claim),
        };
      }),
    [visibleClaims, clinicLabelById, onClaimClick],
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box display='flex' flexWrap='wrap' gap={2} alignItems='center'>
          <FormControl variant='outlined' sx={{ minWidth: 200 }} size='small'>
            <InputLabel>Clinics</InputLabel>
            <Select
              value={clinicIds}
              label='Clinics'
              multiple
              renderValue={(selected) => selected.join(', ')}
              onChange={(e: SelectChangeEvent<string[]>) =>
                setClinicIds(e.target.value as string[])
              }>
              {clinicOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    size='small'
                    checked={clinicIds.includes(option.value)}
                    style={{ padding: '0 8px' }}
                  />
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant='outlined' sx={{ minWidth: 180 }} size='small'>
            <InputLabel>Status</InputLabel>
            <Select
              value={statuses}
              label='Status'
              multiple
              renderValue={(selected) => selected.join(', ')}
              onChange={(e: SelectChangeEvent<string[]>) =>
                setStatuses(e.target.value as string[])
              }>
              {toSelectOptions(Object.values(ClaimStatus)).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    size='small'
                    checked={statuses.includes(option.value)}
                    style={{ padding: '0 8px' }}
                  />
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant='outlined' sx={{ minWidth: 160 }} size='small'>
            <InputLabel>Type</InputLabel>
            <Select
              value={types}
              label='Type'
              multiple
              renderValue={(selected) => selected.join(', ')}
              onChange={(e: SelectChangeEvent<string[]>) => setTypes(e.target.value as string[])}>
              {toSelectOptions(Object.values(ClaimType)).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    size='small'
                    checked={types.includes(option.value)}
                    style={{ padding: '0 8px' }}
                  />
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            size='small'
            multiple
            options={patientOptions}
            getOptionLabel={(option) => option.label}
            value={patientOptions.filter((option) => patientIds.includes(option.value))}
            onChange={(_, value) => setPatientIds(value.map((p) => p.value))}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disabled={patientOptions.length === 0}
            noOptionsText='Apply a filter to load patients'
            renderInput={(params) => (
              <TextField {...params} variant='outlined' label='Patients' sx={{ minWidth: 220 }} />
            )}
            sx={{ minWidth: 240 }}
          />
          <TextField
            label='From'
            type='date'
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size='small'
            variant='outlined'
          />
          <TextField
            label='Until'
            type='date'
            value={untilDate}
            onChange={(e) => setUntilDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size='small'
            variant='outlined'
          />
          <Button
            variant='contained'
            color='primary'
            onClick={handleApply}
            disabled={loading || busy}>
            Apply filter
          </Button>
        </Box>

        {loading && pageProgress && (
          <Alert severity='info'>
            Loading claims (page {pageProgress.page} of {pageProgress.totalPages})…
          </Alert>
        )}

        <BulkActionsToolbar
          selectedCount={selectedRowIds.length}
          resyncableCount={resyncableSelectedIds.length}
          busy={busy}
          canDelete={canDelete}
          onClearSelection={() => setSelectedRowIds([])}
          onConfirmBulkResync={handleBulkResync}
          onConfirmBulkDelete={handleBulkDelete}
        />

        {!loading && claims.length === 0 && (
          <Typography variant='body2' color='text.secondary'>
            No claims loaded yet — pick filters and click Apply filter.
          </Typography>
        )}

        <CustomTable
          columns={COLUMNS}
          rows={rows}
          loading={loading}
          minWidth={800}
          tableSize='small'
          maxHeight={'calc(100vh - 320px)'}
          selection={{
            selectable: true,
            selectedRowIds,
            onSelectionChange: setSelectedRowIds,
          }}
          pagination={{
            rowsPerPageOptions: [PAGE_SIZE],
            rowPerPage: PAGE_SIZE,
            page,
            totalCount: filteredClaims.length,
            onPageChange: (p) => setPage(p),
          }}
        />
      </Stack>
    </Box>
  );
};
